/**
 * Data Quality Card
 *
 * Shows a real measurement-method quality score derived from a server-side
 * MongoDB aggregate (no row-limit). Respects the includeChildren toggle.
 *
 * Visual: large score, tapered stacked column with inline %, legend table below.
 */

import { useMemo } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { UncontrolledTooltip } from 'reactstrap';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { DataQuality } from '../types/emissions';
import { useGetDataQualitySummaryQuery } from '../api/emissions';
import { DATA_QUALITY_COLORS } from '../config/dataQualityBranding';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

interface DataQualityCardProps {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
}

const QUALITY_WEIGHTS: Record<DataQuality, number> = {
  [DataQuality.Measured]: 100,
  [DataQuality.Calculated]: 80,
  [DataQuality.CalculatedFallback]: 60,
  [DataQuality.Estimated]: 30,
  [DataQuality.Failed]: 0,
};

const QUALITY_LABELS: Record<DataQuality, string> = {
  [DataQuality.Measured]: 'Measured',
  [DataQuality.Calculated]: 'Calculated',
  [DataQuality.CalculatedFallback]: 'Calculated (Fallback)',
  [DataQuality.Estimated]: 'Estimated',
  [DataQuality.Failed]: 'Failed',
};

const QUALITY_TOOLTIPS: Record<DataQuality, string> = {
  [DataQuality.Measured]: 'Direct measurement from meters, sensors, or instrumented monitoring. Highest GHG Protocol data quality tier.',
  [DataQuality.Calculated]: 'Derived from activity data multiplied by published emission factors. Standard calculation-based approach.',
  [DataQuality.CalculatedFallback]: 'Calculated using a region or year fallback factor because an exact match was unavailable.',
  [DataQuality.Estimated]: 'Expert estimate or proxy data specific to your organisation. Used when precise measurement is unavailable.',
  [DataQuality.Failed]: 'Calculation failed — no valid emission factor could be found. Review the activity details and retry.',
};

// Top-to-bottom: best quality first
const QUALITY_ORDER: DataQuality[] = [
  DataQuality.Measured,
  DataQuality.Calculated,
  DataQuality.CalculatedFallback,
  DataQuality.Estimated,
  DataQuality.Failed,
];

const COLUMN_HEIGHT = 180;
const COLUMN_WIDTH = 60;

const getScoreColor = (score: number) => {
  if (score >= 90) return 'tw:text-emerald-400';
  if (score >= 70) return 'tw:text-emerald-500';
  if (score >= 50) return 'tw:text-emerald-700';
  if (score >= 30) return 'tw:text-amber-500';
  if (score > 0) return 'tw:text-red-600';
  return 'tw:text-red-600';
};

const getScoreLabel = (score: number) => {
  if (score >= 90) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Fair';
  if (score >= 30) return 'Needs Improvement';
  if (score > 0) return 'Poor';
  return 'No Data';
};

export const DataQualityCard = ({ initiativeId, periodStart, periodEnd, includeChildren }: DataQualityCardProps) => {
  const { data, isLoading } = useGetDataQualitySummaryQuery(
    { initiativeId, periodStart, periodEnd, includeChildren },
    { skip: !initiativeId },
  );

  const total = data?.total ?? 0;

  const overallScore = total > 0
    ? Math.round(
        (
          ((data?.measured ?? 0) * QUALITY_WEIGHTS[DataQuality.Measured]) +
          ((data?.calculated ?? 0) * QUALITY_WEIGHTS[DataQuality.Calculated]) +
          ((data?.calculatedFallback ?? 0) * QUALITY_WEIGHTS[DataQuality.CalculatedFallback]) +
          ((data?.estimated ?? 0) * QUALITY_WEIGHTS[DataQuality.Estimated]) +
          ((data?.failed ?? 0) * QUALITY_WEIGHTS[DataQuality.Failed])
        ) / total,
      )
    : 0;

  const counts: Record<DataQuality, number> = useMemo(() => ({
    [DataQuality.Measured]: data?.measured ?? 0,
    [DataQuality.Calculated]: data?.calculated ?? 0,
    [DataQuality.CalculatedFallback]: data?.calculatedFallback ?? 0,
    [DataQuality.Estimated]: data?.estimated ?? 0,
    [DataQuality.Failed]: data?.failed ?? 0,
  }), [data?.measured, data?.calculated, data?.calculatedFallback, data?.estimated, data?.failed]);

  const nonZeroQualities = useMemo(
    () => QUALITY_ORDER.filter((q) => counts[q] > 0),
    [counts],
  );

  const scoreLabel = getScoreLabel(overallScore);

  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <CardTitle className='tw:flex tw:justify-between tw:items-center tw:gap-2'>
          <div>
            Data Quality
          </div>
          <Badge variant='secondary' className={cn('tw:text-md tw:font-bold', getScoreColor(overallScore))}>
            {scoreLabel}
            <Info className='tw:size-3 tw:text-muted-foreground tw:cursor-help tw:opacity-50' />
          </Badge>
          
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='tw:flex tw:items-center tw:justify-center tw:h-[200px]'>
            <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : total === 0 ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-[200px] tw:text-muted-foreground'>
            <Info className='tw:size-8 tw:mb-2 tw:opacity-50' />
            <p className='tw:text-sm tw:text-center'>Add activities to see your data quality score</p>
          </div>
        ) : (
          <div className='tw:flex tw:flex-col tw:items-center'>
            <div
              className='tw:flex tw:flex-col tw:overflow-hidden tw:rounded-lg'
              style={{
                width: COLUMN_WIDTH,
                height: COLUMN_HEIGHT,
              }}
            >
              {nonZeroQualities.map((quality) => {
                const pct = Math.round((counts[quality] / total) * 100);
                return (
                  <div
                    key={quality}
                    className='tw:w-full tw:flex tw:items-center tw:justify-center'
                    style={{
                      flex: counts[quality],
                      backgroundColor: DATA_QUALITY_COLORS[quality] ?? '#6b7280',
                    }}
                  >
                    {pct >= 8 && (
                      <span className='tw:text-xs tw:text-white tw:drop-shadow-sm'>
                        {pct}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend table */}
            <div className='tw:w-full tw:mt-5 tw:space-y-2'>
              {QUALITY_ORDER.map((quality) => {
                const color = DATA_QUALITY_COLORS[quality] ?? '#6b7280';
                const count = counts[quality];
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={quality} className={cn('tw:flex tw:items-center tw:justify-between tw:text-xs', count === 0 && 'tw:opacity-40')}>
                    <div className='tw:flex tw:items-center tw:gap-2'>
                      <span
                        className='tw:size-2.5 tw:rounded-full tw:shrink-0'
                        style={{ backgroundColor: color }}
                      />
                      <span>{QUALITY_LABELS[quality]}</span>
                      <Info
                        id={`dq-tooltip-${quality}`}
                        className='tw:size-3 tw:text-muted-foreground tw:cursor-help tw:opacity-50'
                      />
                      <UncontrolledTooltip placement='right' target={`dq-tooltip-${quality}`}>
                        {QUALITY_TOOLTIPS[quality]}
                      </UncontrolledTooltip>
                    </div>
                    <span className='tw:text-muted-foreground'>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
